import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  IconButton,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Avatar,
  Divider,
  Card,
  CardContent,
  Chip
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import BedIcon from '@mui/icons-material/Hotel';
import EditNoteIcon from '@mui/icons-material/EditNote';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';
import LocalPharmacyIcon from '@mui/icons-material/LocalPharmacy';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import { useNavigate } from 'react-router-dom';
import VitalsDrawer from './VitalsDrawer';
import DiscussionModal from './DiscussionModal';
import { calculateAge } from '../../utility/general';

const formatDate = (dateString) => {
  return new Date(dateString).toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};


const PatientAdmissionDetails = ({ treatment }) => {
  const navigate = useNavigate();
  const [vitalsOpen, setVitalsOpen] = useState(false);
  const [discussionOpen, setDiscussionOpen] = useState(false);
  const [selectedNote, setSelectedNote] = useState(null);

  // Helper functions to get labels from values
  const getFrequencyLabel = (value) => {
    return administrationFrequencies.find(f => f.value === value)?.label || 'N/A';
  };

  const getStrengthUnitLabel = (value) => {
    return drugStrengthUnits.find(u => u.value === value)?.label || 'N/A';
  };

  const getRouteLabel = (value) => {
    return routesOfAdministration.find(r => r.value === value)?.label || 'N/A';
  };

  // Update Patient Info Card Chips
  const patientChips = [
    {
      label: `Age: ${calculateAge(treatment?.patient?.dateOfBirth)}`,
      value: calculateAge(treatment?.patient?.dateOfBirth)
    },
    {
      label: `Weight: ${treatment?.patient?.weight || 'N/A'}kg`,
      value: treatment?.patient?.weight
    },
    {
      label: `Temperature: ${treatment?.temperature || 'N/A'}°C`,
      value: treatment?.temperature
    }
  ];

  const administrationFrequencies = [
        { value: 0, label: 'Select Frequency' },
        { value: 1, label: 'Immediately' },
        { value: 2, label: 'As needed' },
        { value: 3, label: 'Once daily' },
        { value: 4, label: 'Twice a day' },
        { value: 5, label: 'Three times a day' },
        { value: 6, label: 'Four times a day' },
        { value: 7, label: 'At night' },
        { value: 8, label: 'Morning' },
        { value: 9, label: 'Evening' },
        { value: 10, label: 'Every 24 hours' },
        { value: 11, label: 'Every 12 hours' },
        { value: 12, label: 'Every 8 hours' },
        { value: 13, label: 'Every 6 hours' },
        { value: 14, label: 'Every 4 hours' },
        { value: 15, label: 'Every 3 hours' },
        { value: 16, label: 'Every 2 hours' },
        { value: 17, label: 'Every hour' },
        { value: 18, label: 'Every 2 months' },
        { value: 19, label: 'Every 3 months' },
    ];

    const drugStrengthUnits = [
        { value: -1, label: 'Select Unit' },
        { value: 0, label: "Milligrams" },
        { value: 1, label: "Grams" },
        { value: 2, label: "Micrograms" },
        { value: 3, label: "Milliliters" },
        { value: 4, label: "Liters" },
        { value: 5, label: "Units" },
        { value: 6, label: "Puffs" },
        { value: 7, label: "Sprays" },
        { value: 8, label: "Drops" },
        { value: 9, label: "Patch" },
        { value: 10, label: "Bottle" },
        { value: 11, label: "TransdermalSystem" },
        { value: 12, label: "Tablet" },
        { value: 13, label: "Capsule" },
        { value: 14, label: "Suppository" },
        { value: 15, label: "Scoop" },
        { value: 16, label: "Sachet" },
        { value: 17, label: "Ampoule" },
        { value: 18, label: "Vial" },
        { value: 19, label: "InjectionPen" },
        { value: 20, label: "Enema" },
        { value: 21, label: "Ounces" },
        { value: 22, label: "Teaspoon" },
        { value: 23, label: "Tablespoon" },
        { value: 24, label: "Milliequivalents" },
        { value: 25, label: "InternationalUnits" }
    ];


    const routesOfAdministration = [
        { value: 0, label: 'Select Route' },
        { value: 1, label: 'Orally' },
        { value: 2, label: 'Sublingual' },
        { value: 3, label: 'Topical' },
        { value: 4, label: 'Inhalation' },
        { value: 5, label: 'Suppository' },
        { value: 6, label: 'IV' },
        { value: 7, label: 'IM' },
        { value: 8, label: 'Subcut' },
        { value: 9, label: 'Intradermal' },
        { value: 10, label: 'PerRectum' },
        { value: 11, label: 'PerVagina' },
        { value: 12, label: 'Implant' },
    ];

  const getTotalMedicationCost = () => {
    if (!treatment?.medications || !Array.isArray(treatment.medications)) {
      return 0;
    }
    return treatment.medications.reduce((total, med) => total + (med.cost || 0), 0);
  };

  // Add these handler functions
  const handleVitalsClose = () => setVitalsOpen(false);
  const handleVitalsSave = (data) => {
    // Implement your save logic here
    console.log('Saving vitals:', data);
  };

  const handleDiscussionSubmit = (response) => {
    // Implement your submit logic here
    console.log('Submitting response:', response);
  };

  return (
    <Box sx={{ p: 3, bgcolor: '#f5f5f5', minHeight: '100vh', minWidth: '80vw' }}>
      {/* Header */}
      <Paper 
        elevation={0} 
        sx={{ 
          p: 2, 
          mb: 3, 
          borderRadius: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <IconButton onClick={() => navigate(-1)} sx={{ mr: 2 }}>
            <ArrowBackIcon />
          </IconButton>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 600 }}>
              {treatment?.patient?.firstName} {treatment?.patient?.lastName}
            </Typography>
          </Box>
        </Box>
        <Box>
          <Button 
            variant="contained" 
            sx={{ 
              mr: 2, 
              bgcolor: 'primary',
              '&:hover': { bgcolor: 'secondary.main' }
            }}
            onClick={() => setVitalsOpen(true)}
          >
            View Vitals
          </Button>
          <Button 
            variant="contained"
            sx={{ 
              bgcolor: 'primary',
              '&:hover': { bgcolor: 'secondary.main' }
            }}
            onClick={() => {
              localStorage.setItem("appointmentId", treatment?.appointmentId);
              navigate(`/patients/patient-details/${treatment?.patient?.id}`);
            }}
          >
            Patient File
          </Button>
        </Box>
      </Paper>

      <Grid container spacing={3}>
        {/* Left Column */}
        <Grid item xs={12} md={4}>
          {/* Updated Patient Info Card */}
          <Card sx={{ mb: 3, borderRadius: 2 }}>
            <CardContent>
              <Box sx={{ textAlign: 'center' }}>
                <Avatar
                  sx={{ 
                    width: 120, 
                    height: 120, 
                    mx: 'auto', 
                    mb: 2,
                    border: '4px solid',
                    borderColor: 'primary.light' 
                  }}
                  src={treatment?.patient?.profilePic}
                />
                <Typography variant="h6" sx={{ mb: 1 }}>
                  Patient Details
                </Typography>
              </Box>

              <Box sx={{ mt: 2 }}>
                <Grid container spacing={1}>
                  {patientChips.map((chip, index) => (
                    <Grid item xs={chip.value ? 6 : 12} key={index}>
                      <Chip 
                        label={chip.label}
                        size="small" 
                        color="primary" 
                        variant="outlined"
                        sx={{ width: '100%', ...(index === 2 && { mt: 1 }) }}
                      />
                    </Grid>
                  ))}
                </Grid>

                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    <CalendarTodayIcon sx={{ fontSize: 16, mr: 1, verticalAlign: 'middle' }}/>
                    Visit Date
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 0.5 }}>
                    {new Date(treatment?.dateOfVisit).toLocaleString()}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>

          {/* Hospital Info Card */}
          <Card sx={{ mb: 3, borderRadius: 2 }}>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Hospital Information
              </Typography>
              
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" color="text.secondary">
                  Admitted To
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  {treatment?.admittedHealthCareProvider?.name}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  <LocationOnIcon sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'middle' }}/>
                  {treatment?.admittedHealthCareProvider?.location}
                </Typography>
              </Box>

              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" color="text.secondary">
                  Attending Doctor
                </Typography>
                <Typography variant="body1">
                  Dr. {treatment?.doctor?.firstName} {treatment?.doctor?.lastName}
                </Typography>
              </Box>

              <Box>
                <Typography variant="subtitle2" color="text.secondary">
                  Referred From
                </Typography>
                <Typography variant="body1">
                  {treatment?.initiatedHealthCareProvider?.name}
                </Typography>
              </Box>
            </CardContent>
          </Card>

          {/* Bed Assignment Card */}
          <Card sx={{ borderRadius: 2 }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <BedIcon sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant="h6">
                  Bed Assignment
                </Typography>
              </Box>
              <Box sx={{ 
                bgcolor: 'primary.lighter', 
                p: 2, 
                borderRadius: 1,
                border: '1px solid',
                borderColor: 'primary.light'
              }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  Bed A1 | Ward A
                </Typography>
                <Button 
                  variant="text" 
                  size="small" 
                  sx={{ mt: 1 }}
                >
                  View Details
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Right Column */}
        <Grid item xs={12} md={8}>
          {/* Updated Clinical Details Card */}
          <Card sx={{ mb: 3, borderRadius: 2 }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <LocalHospitalIcon sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant="h6">
                  Clinical Details
                </Typography>
              </Box>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Box sx={{ 
                    bgcolor: '#f8f9fa', 
                    p: 2, 
                    borderRadius: 1,
                    border: '1px solid #e9ecef'
                  }}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Diagnosis
                    </Typography>
                    <Typography variant="body1" sx={{ mt: 1, fontWeight: 500 }}>
                      {treatment?.diagnosis}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      {treatment?.additionalNote}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12}>
                  <Box sx={{ 
                    bgcolor: '#f8f9fa', 
                    p: 2, 
                    borderRadius: 1,
                    border: '1px solid #e9ecef'
                  }}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Medications
                    </Typography>
                    {treatment?.medications?.map((med, index) => (
                      <Typography key={index} variant="body1" sx={{ mt: 1 }}>
                        {`${index + 1}. ${med.pharmacyInventory?.productName}`}
                      </Typography>
                    ))}
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* New Medication Details Card */}
          <Card sx={{ mb: 3, borderRadius: 2 }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <LocalPharmacyIcon sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant="h6">
                  Medication Details
                </Typography>
              </Box>
              
              {treatment?.medications?.map((med, index) => (
                <Box 
                  key={index}
                  sx={{ 
                    mb: 2,
                    p: 2,
                    bgcolor: '#f8f9fa',
                    borderRadius: 1,
                    border: '1px solid #e9ecef'
                  }}
                >
                  <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                    {med.pharmacyInventory?.productName}
                  </Typography>
                  <Grid container spacing={2} sx={{ mt: 1 }}>
                    <Grid item xs={6} md={3}>
                      <Typography variant="caption" color="text.secondary">
                        Quantity & Unit
                      </Typography>
                      <Typography variant="body2">
                        {`${med.quantity} ${getStrengthUnitLabel(med.drugStrengthUnit)}`}
                      </Typography>
                    </Grid>
                    <Grid item xs={6} md={3}>
                      <Typography variant="caption" color="text.secondary">
                        Frequency
                      </Typography>
                      <Typography variant="body2">
                        {getFrequencyLabel(med.administrationFrequency)}
                      </Typography>
                    </Grid>
                    <Grid item xs={6} md={3}>
                      <Typography variant="caption" color="text.secondary">
                        Route
                        Duration
                      </Typography>
                      <Typography variant="body2">
                        {med.duration} days
                      </Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="caption" color="text.secondary">
                        Cost
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        ₦{med.cost.toLocaleString()}
                      </Typography>
                    </Grid>
                  </Grid>
                </Box>
              ))}

              <Box sx={{ 
                mt: 2, 
                p: 2, 
                bgcolor: 'primary.lighter',
                borderRadius: 1,
                border: '1px solid',
                borderColor: 'primary.light'
              }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  Total Medication Cost: ₦{getTotalMedicationCost().toLocaleString()}
                </Typography>
              </Box>
            </CardContent>
          </Card>

          {/* Nurse Notes Card */}
          <Card sx={{ borderRadius: 2 }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <EditNoteIcon sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant="h6">
                  Nurse Notes
                </Typography>
              </Box>
              {treatment?.nurseNotes?.map((note, index) => (
                <Box 
                  key={index} 
                  sx={{ 
                    mb: 2,
                    p: 2,
                    bgcolor: '#f8f9fa',
                    borderRadius: 1,
                    border: '1px solid #e9ecef'
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <AccessTimeIcon sx={{ fontSize: 16, mr: 1, color: 'text.secondary' }} />
                    <Typography variant="caption" color="text.secondary">
                      {new Date(note.timestamp).toLocaleString()}
                    </Typography>
                    <Typography 
                      variant="caption" 
                      sx={{ ml: 'auto' }}
                      color="primary"
                    >
                      {note.nurse}
                    </Typography>
                  </Box>
                  <Typography variant="body2">
                    {note.content}
                  </Typography>
                </Box>
              ))}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <VitalsDrawer 
        open={vitalsOpen}
        onClose={handleVitalsClose}
        initialData={treatment}
        onSave={handleVitalsSave}
      />
      <DiscussionModal 
        open={discussionOpen}
        onClose={() => setDiscussionOpen(false)}
        note={selectedNote}
        onSubmit={handleDiscussionSubmit}
      />
    </Box>
  );
};

export default PatientAdmissionDetails;